import '@/subjects/registerSubjects'
import { gestureGateway } from '@/gestures/gateway'
import { measureReactCommit } from '@/perf/log'
import { Profiler, Suspense, lazy, useEffect, useMemo } from 'react'
import { createStore, Provider as JotaiProvider } from 'jotai'
import { HomeScreen } from './components/PageHome'
import { OverlayPortal } from './overlays/Portal'
import { isLabRoute, useRoute } from './router'

import './App.css'

// Lazy so that lab pages stay out of the product chunk.
const LabRoutes = lazy(() => import('@/lab/LabRoutes'))

// The editor (a document's canvas, the renderers and the tools) is its own chunk: home draws without
// it, and fetches it while idle so opening a world does not wait for it.
const loadDocumentView = () => import('./components/PageDocument')
const DocumentView = lazy(() => loadDocumentView().then((m) => ({ default: m.DocumentView })))
function usePrefetchEditor(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return
    const start = () => { void loadDocumentView() }
    if (typeof requestIdleCallback === 'function') {
      const handle = requestIdleCallback(start, { timeout: 3000 })
      return () => cancelIdleCallback(handle)
    }
    const timer = setTimeout(start, 1500)
    return () => clearTimeout(timer)
  }, [enabled])
}

export default function App() {
  const [route] = useRoute()
  usePrefetchEditor(route.page === 'home' || route.page === 'documents')

  useEffect(() => {
    const unsub = gestureGateway.subscribe(e => {
      if (e.type !== 'KEY_DOWN') return
      const target = (e.originalEvent?.target || document.activeElement) as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return
    })
    return () => { unsub?.() }
  }, [])

  const store = useMemo(() => createStore(), [])

  return (
    <JotaiProvider store={store}>
      <Profiler id="AppRoot" onRender={(id, phase, _actualDuration, _baseDuration, startTime, commitTime) => {
        // Record commit durations into the User Timing timeline
        measureReactCommit(`react:${id}:${phase}`, startTime, commitTime)
      }}>
        <OverlayPortal>
          {/* Route-based document identity */}
          {route.page === 'home' && <HomeScreen />}
          {route.page === 'documents' && <HomeScreen />}
          {route.page === 'local' && (
            <Suspense fallback={null}>
              <DocumentView id={route.id} key={route.id} />
            </Suspense>
          )}
          {/* Experimental pages live in src/lab and are code-split out of the product bundle */}
          {isLabRoute(route) && (
            <Suspense fallback={null}>
              <LabRoutes route={route} />
            </Suspense>
          )}
        </OverlayPortal>
      </Profiler>
    </JotaiProvider>
  )
}
